import moment from 'moment-timezone'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { fromZonedTime } from 'date-fns-tz'
import { startOfDay, endOfDay } from 'date-fns'
import DatePicker from 'react-datepicker'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { buildCsvFilename, downloadCsv } from '@/utils/file'

import { TIMEZONES } from '../../../helpers/constants'
import { useSnackbar } from '../../../helpers/hooks/useSnackbar'
import { getTimezoneOffset } from '../../../helpers/helpers'
import { clearAllFilters, selectAllItems, selectItem } from '../../../reducers/filtersReducer'
import { selectUserData } from '../../../reducers/userDataReducer'
import { selectActiveBuilding } from '../../../reducers/buildingSelectorReducer'
import Icon from '../../Icons'

import API from './../../../api/api'
import { getCalendarMonthRange } from '../helpers'
import DropdownList from './DropdownList'
import SyncCalendar from './SyncCalendar'

const Sidebar = ({
  map,
  clearAllFilters,
  onCreateEvent,
  onCreateReservation,
  isEvent,
  getReservationsByBuildingId,
  selectItem,
  getAmenitiesByBuildingId,
  getTrainingsByBuildingId,
  data,
  filters,
  userData,
  selectAllItems,
  activeBuilding,
}) => {
  const { t } = useTranslation(['calendar'])
  const { displaySnackbar } = useSnackbar()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)

  const isReservation = !isEvent
  const { role } = userData.user
  const proTrainer = role === 'Trainer' && userData.services && userData.services.length > 0
  const buildingTimezone = activeBuilding ? activeBuilding.timezone : 'America/Chicago'

  const hasAmenityLocations = () => {
    const selectedAmenities = filters.calendar['Choose Reservation']
    if (selectedAmenities.length === 0) {
      return false
    }
    return selectedAmenities.some((selected) => {
      const amenity = data.find((a) => a.id === selected.id)
      return amenity && amenity.locations && amenity.locations.length > 0
    })
  }

  // Check if resident events feature is available
  const hasResidentEvents = activeBuilding?.residentEventsAvailable || false
  const residentEventsUrl = hasResidentEvents
    ? '/dashboard/resident-events'
    : '/dashboard/upselling/residentEvents'

  useEffect(() => {
    return () => {
      clearAllFilters('calendar')
    }
  }, [])

  const handleSelect = async (name, item) => {
    const isReservation = !isEvent

    if (name === 'Location') {
      const { start, end } = getCalendarMonthRange(new Date(), buildingTimezone)

      await selectItem('calendar', name, item, false)

      if (activeBuilding && item?.id) {
        if (isEvent) {
          getTrainingsByBuildingId(start, end, item.id)
        }
        if (isReservation) {
          getReservationsByBuildingId(start, end, item.id)
          getAmenitiesByBuildingId(item.id)
        }
      }
    } else {
      selectItem('calendar', name, item, true)
    }
  }

  useEffect(() => {
    if (activeBuilding) {
      handleSelect('Location', activeBuilding)
    }
  }, [activeBuilding])

  const handleCreateEvent = (e) => {
    e.preventDefault()
    onCreateEvent()
  }

  const handleAddReservation = (e) => {
    e.preventDefault()
    onCreateReservation()
  }

  const handleSelectAll = (name) => {
    selectAllItems('calendar', name)
  }

  const downloadCSVList = (e) => {
    e.preventDefault()
    setLoading(true)
    const fields = [
      {
        label: t('calendar:csvExport.day'),
        value: (row) => moment(row?.date).tz(row?.buildingId?.timezone).format('ddd'),
      },
      {
        label: t('calendar:csvExport.date'),
        value: (row) => moment(row?.date).tz(row?.buildingId?.timezone).format('MM/DD/YYYY'),
      },
      {
        label: t('calendar:csvExport.time'),
        value: (row) =>
          `${moment(row?.date)
            .tz(row?.buildingId?.timezone)
            .utcOffset(getTimezoneOffset(row?.buildingId?.timezone))
            .format('hh:mmA')} - ${moment(row?.date)
            .add(row?.duration, 'm')
            .tz(row?.buildingId?.timezone)
            .utcOffset(getTimezoneOffset(row?.buildingId?.timezone))
            .format('hh:mmA')} ${Object.keys(TIMEZONES).find(
            (key) => TIMEZONES[key] === row?.buildingId?.timezone,
          )}`,
      },
      {
        label: t('calendar:csvExport.location'),
        value: 'buildingId.name',
      },
      {
        label: t('calendar:csvExport.staff'),
        value: (row) =>
          `${row?.trainerId?.firstName || ''} ${row?.trainerId?.lastName || ''}`.trim(),
      },
      {
        label: t('calendar:sidebar.eventType'),
        value: 'type.name',
      },
      {
        label: t('calendar:csvExport.registered'),
        value: 'taken',
      },
      {
        label: t('calendar:csvExport.capacity'),
        value: 'capacity',
      },
      {
        label: t('calendar:csvExport.checkIn'),
        value: (row) => [...(row?.clients || []).filter((client) => client.participation)].length,
      },
      {
        label: t('calendar:csvExport.description'),
        value: 'description',
      },
    ]

    API.getTrainingsByBuildingId(
      startOfDay(fromZonedTime(startDate, buildingTimezone)).getTime(),
      endOfDay(fromZonedTime(endDate, buildingTimezone)).getTime(),
      activeBuilding?.id,
    )
      .then((trainings) => {
        downloadCsv({
          data: trainings.sort((a, b) => new Date(a.date) - new Date(b.date)),
          fields,
          filename: buildCsvFilename({
            building: activeBuilding?.name || null,
            page: 'calendar',
            dateRange: [startDate, endDate],
            timezone: buildingTimezone,
          }),
        })
      })
      .catch(() => {
        displaySnackbar(t('calendar:errors.loadTrainings'), true)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const setDateRange = (updatedDateRange) => {
    setStartDate(updatedDateRange[0])
    setEndDate(updatedDateRange[1])
  }

  return (
    <div className="ctp-sidebar">
      {(['Manager', 'Administrator', 'PropertyManager', 'ShiftManager'].includes(role) ||
        proTrainer) &&
        isEvent && (
          <button className="ctp-sidebar-button" onClick={handleCreateEvent}>
            {t('calendar:sidebar.create')}
          </button>
        )}
      {['Administrator', 'PropertyManager', 'ShiftManager'].includes(role) && isReservation && (
        <button
          disabled={['Administrator', 'PropertyManager'].includes(role) && !activeBuilding}
          className="ctp-sidebar-button"
          onClick={handleAddReservation}
        >
          {t('calendar:sidebar.addReservation')}
        </button>
      )}
      {['Manager', 'Administrator'].includes(role) && isEvent && (
        <DropdownList
          label={t('calendar:sidebar.staffMember')}
          name="Staff Member"
          selectAllText={t('calendar:sidebar.allMembers')}
          items={activeBuilding ? map('Staff Member', data) : []}
          filters={filters.calendar}
          options={{
            hasSearch: true,
            hasSelectAll: true,
            hasSelection: !!activeBuilding,
            selectAllByDefault: true,
            allowMultiple: true,
            filterAllAvatar: 'clothes',
          }}
          onSelect={handleSelect}
          onSelectAll={handleSelectAll}
          sidebarOption={true}
        />
      )}
      {isEvent ? (
        <DropdownList
          label={t('calendar:sidebar.eventType')}
          name="Class Type"
          selectAllText={t('calendar:sidebar.allTypes')}
          items={
            ['PropertyManager', 'ShiftManager', 'Trainer'].includes(role) || activeBuilding
              ? map('Class Type', data)
              : []
          }
          filters={filters.calendar}
          options={{
            hasSearch: true,
            hasSelectAll: true,
            hasSelection: !!activeBuilding,
            selectAllByDefault: true,
            allowMultiple: true,
            filterAllAvatar: 'sports',
          }}
          onSelect={handleSelect}
          onSelectAll={handleSelectAll}
          sidebarOption={true}
        />
      ) : (
        <DropdownList
          label={t('calendar:sidebar.selectAmenity')}
          name="Choose Reservation"
          selectAllText={t('calendar:sidebar.allReservations')}
          items={
            ['PropertyManager', 'ShiftManager'].includes(role) || activeBuilding
              ? map('Choose Reservation', data)
              : []
          }
          filters={filters.calendar}
          options={{
            hasSearch: true,
            hasSelectAll: true,
            hasSelection: ['PropertyManager', 'ShiftManager'].includes(role) || !!activeBuilding,
            selectAllByDefault: true,
            allowMultiple: true,
          }}
          onSelect={handleSelect}
          onSelectAll={handleSelectAll}
          sidebarOption={true}
        />
      )}
      {isReservation && hasAmenityLocations() && (
        <DropdownList
          label={t('calendar:sidebar.selectSpace')}
          name="Select Space"
          selectAllText={t('calendar:sidebar.allSpaces')}
          items={map('Select Space', data)}
          filters={filters.calendar}
          options={{
            hasSearch: true,
            hasSelectAll: true,
            hasSelection: true,
            selectAllByDefault: true,
            allowMultiple: true,
          }}
          onSelect={handleSelect}
          onSelectAll={handleSelectAll}
          sidebarOption={true}
        />
      )}
      {role === 'Trainer' && filters.calendar.eventType === 'appointment' ? (
        <DropdownList
          label={t('calendar:sidebar.chooseClient')}
          name="Choose Client"
          selectAllText={t('calendar:sidebar.allClients')}
          items={map('Choose Client', data)}
          filters={filters.calendar}
          options={{
            hasSearch: true,
            hasSelection: true,
            hasSelectAll: true,
            selectAllByDefault: true,
            allowMultiple: true,
            filterAllAvatar: 'clothes',
          }}
          onSelect={handleSelect}
          onSelectAll={handleSelectAll}
        />
      ) : null}
      {['Administrator', 'Manager'].includes(role) && isEvent && (
        <React.Fragment>
          <DatePicker
            selectsRange={true}
            startDate={startDate}
            endDate={endDate}
            placeholderText={t('calendar:sidebar.selectDateRangePlaceholder')}
            popperPlacement="top-end"
            onChange={(update) => {
              console.log(update)
              setDateRange(update)
            }}
          />
          <div className="download-buttons">
            <button
              disabled={!startDate || !endDate}
              className={`button button-primary has-icon ${loading ? 'is-loading' : ''}`}
              onClick={downloadCSVList}
            >
              <Icon icon="download" />
              {t('calendar:sidebar.download')}
            </button>
          </div>
        </React.Fragment>
      )}
      {role === 'PropertyManager' && (
        <>
          <button
            className="ctp-sidebar-button ctp-sidebar-button--explore-events"
            onClick={() => navigate(residentEventsUrl)}
          >
            {t('calendar:sidebar.exploreEventLibrary')}
          </button>
          <SyncCalendar />
        </>
      )}
    </div>
  )
}

Sidebar.propTypes = {
  data: PropTypes.array,
  map: PropTypes.func,
  isEvent: PropTypes.bool,
  onCreateEvent: PropTypes.func,
  onCreateReservation: PropTypes.func,
  filters: PropTypes.object,
  userData: PropTypes.object,
  clearAllFilters: PropTypes.func,
  selectItem: PropTypes.func,
  selectAllItems: PropTypes.func,
  getTrainingsByBuildingId: PropTypes.func,
  getReservationsByBuildingId: PropTypes.func,
  getAmenitiesByBuildingId: PropTypes.func,
  activeBuilding: PropTypes.object,
}

export default connect(
  (state) => ({
    userData: selectUserData(state),
    filters: state.filters,
    activeBuilding: selectActiveBuilding(state),
  }),
  { clearAllFilters, selectItem, selectAllItems },
)(Sidebar)
